import clsx from 'clsx';
import React, {
  ChangeEvent,
  InputHTMLAttributes,
  ReactNode,
  useEffect,
  useState,
} from 'react';

import { SvgIconNames } from '~/design-tokens/iconography/SvgIcons';

import { Icon } from '../icon/Icon';
import styles from './Toggle.module.scss';

export type IconVisibility = 'only-on' | 'only-off' | 'both' | 'none';
export type HighlightMode = 'both' | 'only-on';

export interface ToggleProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'onToggle'> {
  id?: string;
  isChecked?: boolean;
  onToggle?: (checked: boolean, event: ChangeEvent<HTMLInputElement>) => void;
  leftLabel?: ReactNode;
  rightLabel?: ReactNode;
  icon?: SvgIconNames;
  iconVisibility?: IconVisibility;
  labelHighlight?: HighlightMode;
  backgroundHighlight?: HighlightMode;
  size?: 'medium' | 'small';
  disabled?: boolean;
}

export const Toggle = ({
  id,
  isChecked = false,
  onToggle,
  leftLabel,
  rightLabel,
  icon = 'Checkmark',
  iconVisibility = 'only-on',
  labelHighlight = 'both',
  backgroundHighlight = 'only-on',
  size = 'small',
  className,
  disabled = false,
  ...rest
}: ToggleProps) => {
  const [checked, setChecked] = useState(isChecked);

  useEffect(() => {
    setChecked(isChecked);
  }, [isChecked]);

  const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.checked;

    if (onToggle) {
      onToggle(newValue, e);
    }
  };

  const shouldShowIcon =
    iconVisibility == 'both' ||
    (!checked && iconVisibility === 'only-off') ||
    (checked && iconVisibility === 'only-on');

  const isLeftSolid =
    (!checked && labelHighlight === 'only-on') || labelHighlight === 'both';
  const isRightSolid =
    (checked && labelHighlight === 'only-on') || labelHighlight === 'both';
  const isBackgroundActive =
    (checked && backgroundHighlight === 'only-on') ||
    backgroundHighlight === 'both';

  return (
    <label
      htmlFor={id}
      className={clsx(styles.root, className, {
        [styles.disabled]: disabled,
      })}
    >
      {leftLabel && (
        <span
          className={clsx(styles.labelText, {
            [styles.labelSolid]: isLeftSolid,
          })}
          data-testid="toggle-left-text"
        >
          {leftLabel}
        </span>
      )}
      <input
        className={styles.toggleInput}
        id={id}
        type="checkbox"
        role="switch"
        checked={checked}
        aria-checked={checked}
        onChange={handleOnChange}
        data-testid="toggle-ds"
        {...rest}
      />
      <span
        className={clsx(styles.container, styles[size], {
          [styles.backgroundActive]: isBackgroundActive,
          [styles.checked]: checked,
        })}
        data-testid="toggle-container"
        data-toggle-container
      >
        <span
          className={clsx(
            styles.circle,
            size === 'medium' ? styles.circleMedium : styles.circleSmall,
            { [styles.circleChecked]: checked }
          )}
          data-toggle-circle
        >
          {icon && (
            <Icon
              className={clsx(styles.icon, {
                [styles.iconVisible]: shouldShowIcon,
              })}
              iconName={icon}
              size={size === 'medium' ? 14 : 10}
              data-toggle-icon
            />
          )}
        </span>
      </span>
      {rightLabel && (
        <span
          className={clsx(styles.labelText, {
            [styles.labelSolid]: isRightSolid,
          })}
        >
          {rightLabel}
        </span>
      )}
    </label>
  );
};
